import { create } from 'zustand'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import { getBookById, getNextChapter, getPrevChapter, parseReference } from '../lib/bibleBooks'

interface ChapterContent {
  content: string
  reference: string
  translation: string
  verse_count: number | null
  book_id: string
  chapter: number
}

type BibleView = 'books' | 'chapters' | 'reading'

interface ReaderPanel {
  open: boolean
  reference: string
  verse?: number
  bookId: string | null
  chapter: number | null
  content: ChapterContent | null
  loading: boolean
  error: string | null
}

interface BibleState {
  view: BibleView
  selectedBookId: string | null
  selectedChapter: number | null
  translation: string
  chapterContent: ChapterContent | null
  loading: boolean
  error: string | null

  readerPanel: ReaderPanel

  setView: (view: BibleView) => void
  selectBook: (bookId: string) => void
  selectChapter: (chapter: number) => Promise<void>
  setTranslation: (translation: string) => Promise<void>
  goToChapter: (bookId: string, chapter: number) => Promise<void>
  goNext: () => Promise<void>
  goPrev: () => Promise<void>
  goBack: () => void
  openReaderPanel: (reference: string, version?: string) => Promise<void>
  closeReaderPanel: () => void
  readerGoToChapter: (bookId: string, chapter: number) => Promise<void>
  readerGoNext: () => Promise<void>
  readerGoPrev: () => Promise<void>
  readerSetTranslation: (translation: string) => Promise<void>
}

const EMPTY_READER: ReaderPanel = {
  open: false, reference: '', bookId: null, chapter: null,
  content: null, loading: false, error: null,
}

export const useBibleStore = create<BibleState>((set, get) => ({
  view: 'books',
  selectedBookId: null,
  selectedChapter: null,
  translation: 'KJV',
  chapterContent: null,
  loading: false,
  error: null,

  readerPanel: { ...EMPTY_READER },

  setView: (view) => set({ view }),

  selectBook: (bookId) => {
    const book = getBookById(bookId)
    if (book && book.chapters === 1) {
      get().goToChapter(bookId, 1)
    } else {
      set({ selectedBookId: bookId, view: 'chapters' })
    }
  },

  selectChapter: async (chapter) => {
    const { selectedBookId, translation } = get()
    if (!selectedBookId) return
    await get().goToChapter(selectedBookId, chapter)
  },

  setTranslation: async (translation) => {
    const { selectedBookId, selectedChapter, readerPanel, translation: prev } = get()
    if (prev !== translation) {
      track('bible_translation_changed', { from: prev, to: translation, surface: readerPanel.open ? 'reader_panel' : 'bible_page' })
    }
    set({ translation })
    if (readerPanel.open && readerPanel.bookId && readerPanel.chapter) {
      await get().readerGoToChapter(readerPanel.bookId, readerPanel.chapter)
    } else if (selectedBookId && selectedChapter) {
      await get().goToChapter(selectedBookId, selectedChapter)
    }
  },

  goToChapter: async (bookId, chapter) => {
    const { translation } = get()
    set({ loading: true, error: null, selectedBookId: bookId, selectedChapter: chapter, view: 'reading' })
    track('bible_chapter_opened', { book_id: bookId, chapter, translation })
    try {
      const data = await apiClient.get<ChapterContent>(
        `bible/chapter/${bookId}/${chapter}/?translation=${encodeURIComponent(translation)}`
      )
      set({ chapterContent: data, loading: false })
    } catch {
      set({ error: 'Could not load chapter', loading: false })
    }
  },

  goNext: async () => {
    const { selectedBookId, selectedChapter } = get()
    if (!selectedBookId || !selectedChapter) return
    const next = getNextChapter(selectedBookId, selectedChapter)
    if (next) await get().goToChapter(next.bookId, next.chapter)
  },

  goPrev: async () => {
    const { selectedBookId, selectedChapter } = get()
    if (!selectedBookId || !selectedChapter) return
    const prev = getPrevChapter(selectedBookId, selectedChapter)
    if (prev) await get().goToChapter(prev.bookId, prev.chapter)
  },

  goBack: () => {
    const { view, selectedBookId } = get()
    if (view === 'reading') {
      const book = selectedBookId ? getBookById(selectedBookId) : null
      if (book && book.chapters === 1) {
        set({ view: 'books', chapterContent: null, selectedChapter: null })
      } else {
        set({ view: 'chapters', chapterContent: null, selectedChapter: null })
      }
    } else if (view === 'chapters') {
      set({ view: 'books', selectedBookId: null })
    }
  },

  openReaderPanel: async (reference, version) => {
    const parsed = parseReference(reference)
    if (!parsed) return
    if (version) set({ translation: version })
    set((s) => ({
      readerPanel: { ...s.readerPanel, open: true, reference, verse: parsed.verse, bookId: parsed.bookId, chapter: parsed.chapter, loading: true, error: null },
    }))
    await get().readerGoToChapter(parsed.bookId, parsed.chapter)
  },

  closeReaderPanel: () => {
    set({ readerPanel: { ...EMPTY_READER } })
  },

  readerGoToChapter: async (bookId, chapter) => {
    const { translation } = get()
    set((s) => ({
      readerPanel: { ...s.readerPanel, bookId, chapter, loading: true, error: null },
    }))
    try {
      const data = await apiClient.get<ChapterContent>(
        `bible/chapter/${bookId}/${chapter}/?translation=${encodeURIComponent(translation)}`
      )
      set((s) => ({
        readerPanel: { ...s.readerPanel, content: data, loading: false },
      }))
    } catch {
      set((s) => ({
        readerPanel: { ...s.readerPanel, error: 'Could not load chapter', loading: false },
      }))
    }
  },

  readerGoNext: async () => {
    const { readerPanel } = get()
    if (!readerPanel.bookId || !readerPanel.chapter) return
    const next = getNextChapter(readerPanel.bookId, readerPanel.chapter)
    if (next) {
      set((s) => ({ readerPanel: { ...s.readerPanel, verse: undefined } }))
      await get().readerGoToChapter(next.bookId, next.chapter)
    }
  },

  readerGoPrev: async () => {
    const { readerPanel } = get()
    if (!readerPanel.bookId || !readerPanel.chapter) return
    const prev = getPrevChapter(readerPanel.bookId, readerPanel.chapter)
    if (prev) {
      set((s) => ({ readerPanel: { ...s.readerPanel, verse: undefined } }))
      await get().readerGoToChapter(prev.bookId, prev.chapter)
    }
  },

  readerSetTranslation: async (translation) => {
    const { readerPanel, translation: prev } = get()
    if (prev !== translation) {
      track('bible_translation_changed', { from: prev, to: translation, surface: 'reader_panel' })
    }
    set({ translation })
    if (readerPanel.bookId && readerPanel.chapter) {
      await get().readerGoToChapter(readerPanel.bookId, readerPanel.chapter)
    }
  },
}))
